import { getLegalShieldLink } from '../../../config/legalshield-links';
import { trackAffiliateClick } from '../../../utils/analytics';

export default function IdentityPath() {
  const handleClick = () => {
    trackAffiliateClick('Identity Plan', getLegalShieldLink('IDENTITY_PLAN_URL'), 'Home');
  };

  return (
    <section id="path-identity" className="relative py-20 bg-[#0f1d2e]">
      <div className="max-w-5xl mx-auto px-4 sm:px-6">
        {/* Section Header */}
        <div className="text-center mb-12">
          <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-black text-white mb-6" style={{ fontFamily: "'Roboto', sans-serif" }}>
            PATH 3: I ONLY CARE ABOUT <span className="text-[#DC2626]">IDENTITY THEFT</span>
          </h2>
          
          <p className="text-lg sm:text-xl text-gray-300 leading-relaxed max-w-3xl mx-auto mb-8">
            For $19.95/month, you get comprehensive identity theft protection with credit monitoring 
            across all 3 bureaus, dark web monitoring, social media monitoring, and full restoration 
            services backed by $1M identity theft insurance.
          </p>

          {/* Key Bullets */}
          <div className="max-w-2xl mx-auto mb-10">
            <div className="flex items-start gap-3 sm:gap-4 mb-4">
              <i className="ri-shield-check-fill text-[#DC2626] text-xl sm:text-2xl flex-shrink-0 mt-1"></i>
              <p className="text-gray-300 text-base sm:text-lg text-left">
                <strong className="text-white">Real-time monitoring</strong> across credit bureaus, dark web, and social media for any signs of identity theft or fraud.
              </p>
            </div>
            <div className="flex items-start gap-3 sm:gap-4 mb-4">
              <i className="ri-alarm-warning-fill text-[#DC2626] text-xl sm:text-2xl flex-shrink-0 mt-1"></i>
              <p className="text-gray-300 text-base sm:text-lg text-left">
                <strong className="text-white">Instant alerts</strong> the moment something suspicious happens with your identity or credit.
              </p>
            </div>
            <div className="flex items-start gap-3 sm:gap-4">
              <i className="ri-user-settings-fill text-[#DC2626] text-xl sm:text-2xl flex-shrink-0 mt-1"></i>
              <p className="text-gray-300 text-base sm:text-lg text-left">
                <strong className="text-white">Licensed investigators</strong> who handle the entire restoration process if your identity is stolen – you don't lift a finger.
              </p>
            </div>
          </div>
        </div>

        {/* 3-Step Instructions */}
        <div className="bg-gradient-to-br from-[#0a1628] to-[#1a2332] rounded-2xl p-6 sm:p-8 md:p-10 border-2 border-[#DC2626]/30 mb-10">
          <h3 className="text-xl sm:text-2xl font-black text-white mb-6 sm:mb-8 text-center">
            Here's Exactly What To Do:
          </h3>
          
          <div className="space-y-4 sm:space-y-6 mb-8 sm:mb-10">
            {/* Step 1 */}
            <div className="flex items-start gap-4 sm:gap-6">
              <div className="w-10 h-10 sm:w-12 sm:h-12 bg-[#DC2626] rounded-full flex items-center justify-center flex-shrink-0">
                <span className="text-white font-black text-lg sm:text-xl">1</span>
              </div>
              <div>
                <h4 className="text-white font-black text-lg sm:text-xl mb-2">Click the button below</h4>
                <p className="text-gray-300 text-base sm:text-lg">
                  This will take you directly to the IDShield enrollment page.
                </p>
              </div>
            </div>

            {/* Step 2 */}
            <div className="flex items-start gap-4 sm:gap-6">
              <div className="w-10 h-10 sm:w-12 sm:h-12 bg-[#DC2626] rounded-full flex items-center justify-center flex-shrink-0">
                <span className="text-white font-black text-lg sm:text-xl">2</span>
              </div>
              <div>
                <h4 className="text-white font-black text-lg sm:text-xl mb-2">Choose your coverage level</h4>
                <p className="text-gray-300 text-base sm:text-lg">
                  Select individual coverage or add family members. The base plan starts at $19.95/month 
                  for comprehensive identity protection.
                </p>
              </div>
            </div>

            {/* Step 3 */}
            <div className="flex items-start gap-4 sm:gap-6">
              <div className="w-10 h-10 sm:w-12 sm:h-12 bg-[#DC2626] rounded-full flex items-center justify-center flex-shrink-0">
                <span className="text-white font-black text-lg sm:text-xl">3</span>
              </div>
              <div>
                <h4 className="text-white font-black text-lg sm:text-xl mb-2">Complete checkout</h4>
                <p className="text-gray-300 text-base sm:text-lg">
                  Fill out the simple form. You'll immediately get access to your monitoring dashboard 
                  and fraud resolution specialists.
                </p>
              </div>
            </div>
          </div>

          {/* Primary CTA Button */}
          <div className="text-center">
            <a
              href={getLegalShieldLink('IDENTITY_PLAN_URL')}
              target="_blank"
              rel="noopener noreferrer"
              onClick={handleClick}
              className="inline-flex items-center justify-center gap-2 sm:gap-3 bg-[#DC2626] hover:bg-[#B91C1C] text-white font-black text-sm sm:text-base md:text-lg lg:text-xl px-6 sm:px-8 md:px-10 lg:px-12 py-4 sm:py-5 md:py-6 rounded-lg transition-all duration-300 shadow-2xl hover:shadow-[#DC2626]/50 hover:scale-105 cursor-pointer w-full sm:w-auto"
            >
              <span className="hidden sm:inline">PROTECT MY IDENTITY</span>
              <span className="sm:hidden">GET PROTECTED</span>
              <i className="ri-arrow-right-line text-lg sm:text-xl md:text-2xl"></i>
            </a>
          </div>
        </div>

        {/* Micro-copy */}
        <div className="text-center space-y-2 text-gray-400 text-sm sm:text-base max-w-2xl mx-auto">
          <p>
            On the next page you'll see coverage options. Pick the one that fits your household.
          </p>
          <p>
            You might see links about "associates" or "business opportunity." Ignore all of that. You're just grabbing identity protection.
          </p>
        </div>
      </div>
    </section>
  );
}